import React, { useState } from "react";
import { toast } from "sonner";
import { Landmark } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { finApi, finErr, money } from "@/lib/financeApi";
import { btnGold, btnRed, ErrorBox, field, Loading, useLoad } from "@/components/nds/windows/finance/parts";

const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
const L = ({ t, children }) => <label className="block min-w-0 space-y-1"><span className="text-[11px] text-nex-muted">{t}</span>{children}</label>;

function Owed({ o }) {
  const cards = [["Rent collected", o.rent_collected], ["On invoices not paid yet (held back)", -o.rent_waiting], ["Paid to landlord", -o.landlord_paid],
    ["Costs landlord reimburses (Wi-Fi etc.)", -o.deducted], ["Owed from money received", o.owed_received]];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="pay-ll-owed">
      {cards.map(([k, v], i) => (
        <div key={k} className={`p-2 rounded-sm border ${i === 4 ? "border-nex-gold/50" : "border-white/10"}`}>
          <div className="text-[10px] text-nex-muted leading-tight">{k}</div>
          <div className={`font-mono text-sm ${i === 4 ? "text-nex-gold" : "text-nex-silver"}`} data-testid={`pay-ll-owed-${i}`}>{money(v)}</div>
        </div>))}
      <div className="col-span-full text-[10px] text-nex-muted">Since {o.since}. Rent on {o.rent_waiting_count} invoice line(s) the employer hasn't paid yet ({money(o.rent_waiting)}) is held back until the invoice is paid. Counting it, the total owed would be {money(o.owed_to_landlord)}.</div>
    </div>
  );
}

function Residents({ all, value, onChange }) {
  const flip = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div className="max-h-28 overflow-auto border border-white/10 rounded-sm p-2 grid grid-cols-2 gap-x-3 gap-y-1" data-testid="pay-ll-residents">
      {all.map((r) => <label key={r.id} className="flex items-center gap-1.5 text-[11px] text-nex-silver"><input type="checkbox" checked={value.includes(r.id)} onChange={() => flip(r.id)} data-testid={`pay-ll-res-${r.id}`} />{r.name}</label>)}
    </div>
  );
}

function PayForm({ d, onSaved, prefill }) {
  const [f, setF] = useState({ date: today(), amount: "", method: "check", check_number: "", vendor_id: d.landlords[0]?.id || "", payee: "", covers_from: "", covers_to: "", resident_ids: [], memo: "", ...(prefill || {}) });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const save = async () => {
    setBusy(true);
    try {
      const r = await finApi.post("/landlord/pay", { ...f, amount: Number(f.amount), vendor_id: f.vendor_id || null });
      toast.success(`Recorded ${money(r.payment.amount)} to ${r.payment.payee} · owed now ${money(r.owed.owed_to_landlord)}`);
      if (r.notice) toast.info(r.notice, { duration: 9000 });
      onSaved();
    } catch (e) { toast.error(finErr(e)); setBusy(false); }
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 min-w-0" data-testid="pay-ll-form">
      <L t="Date paid"><input type="date" value={f.date} onChange={set("date")} className={`${field} w-full min-w-0`} data-testid="pay-ll-date" /></L>
      <L t="Amount"><div className="flex gap-2"><input type="number" step="0.01" min="0" value={f.amount} onChange={set("amount")} className={`${field} w-full min-w-0`} data-testid="pay-ll-amount" />
        <button type="button" onClick={() => set("amount")(String(Math.max(0, d.owed.owed_received).toFixed(2)))} className="shrink-0 text-[11px] text-nex-gold hover:underline" data-testid="pay-ll-fill">Pay owed</button></div></L>
      <L t="Paid by"><select value={f.method} onChange={set("method")} className={`${field} w-full min-w-0`} data-testid="pay-ll-method">{d.methods.map((m) => <option key={m} value={m}>{m.replace("_", " ")}</option>)}</select></L>
      <L t={f.method === "check" ? "Check number" : "Reference (optional)"}><input value={f.check_number} onChange={set("check_number")} className={`${field} w-full min-w-0`} data-testid="pay-ll-check" /></L>
      <L t="Paid to"><select value={f.vendor_id} onChange={set("vendor_id")} className={`${field} w-full min-w-0`} data-testid="pay-ll-vendor">{d.landlords.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}<option value="">Someone else…</option></select></L>
      {!f.vendor_id ? <L t="Payee name"><input value={f.payee} onChange={set("payee")} className={`${field} w-full min-w-0`} data-testid="pay-ll-payee" /></L> : <div />}
      <L t="Covers from"><input type="date" value={f.covers_from} onChange={set("covers_from")} className={`${field} w-full min-w-0`} data-testid="pay-ll-from" /></L>
      <L t="Covers to"><input type="date" value={f.covers_to} onChange={set("covers_to")} className={`${field} w-full min-w-0`} data-testid="pay-ll-to" /></L>
      <div className="sm:col-span-2"><L t={`Residents covered (${f.resident_ids.length} picked, optional)`}><Residents all={d.residents} value={f.resident_ids} onChange={set("resident_ids")} /></L></div>
      <div className="sm:col-span-2"><L t="Memo"><input value={f.memo} onChange={set("memo")} placeholder="e.g. hand delivered to Joe at Kingston" className={`${field} w-full min-w-0`} data-testid="pay-ll-memo" /></L></div>
      <div className="sm:col-span-2"><button type="button" onClick={save} disabled={busy || !(Number(f.amount) > 0)} className={btnGold} data-testid="pay-ll-save">{busy ? "Saving…" : "Record payment"}</button></div>
    </div>
  );
}

function Recent({ rows, onChanged }) {
  const voidIt = async (p) => {
    const reason = window.prompt(`Void ${money(p.amount)} paid ${p.date}? Reason:`);
    if (!reason) return;
    try { await finApi.post(`/landlord/pay/${p.id}/void`, { reason }); toast.success("Payment voided"); onChanged(); } catch (e) { toast.error(finErr(e)); }
  };
  if (!rows.length) return <div className="text-[11px] text-nex-muted" data-testid="pay-ll-recent-empty">No payments recorded here yet.</div>;
  return (
    <div className="space-y-1 max-h-40 overflow-auto" data-testid="pay-ll-recent">
      {rows.map((p) => (
        <div key={p.id} className={`flex items-center gap-2 text-[11px] ${p.voided ? "opacity-50 line-through" : ""}`} data-testid={`pay-ll-row-${p.id}`}>
          <span className="font-mono w-20">{p.date}</span><span className="font-mono w-20 text-right">{money(p.amount)}</span>
          <span className="flex-1 text-nex-muted truncate" title={p.notes}>{p.payee} · {p.method}{p.check_number ? ` #${p.check_number}` : ""}{p.covers_from ? ` · ${p.covers_from} → ${p.covers_to || "?"}` : ""}</span>
          {!p.voided && <button type="button" onClick={() => voidIt(p)} className={btnRed} data-testid={`pay-ll-void-${p.id}`}>Void</button>}
        </div>))}
    </div>
  );
}

function PayLandlordDialog({ onClose, onChanged, prefill }) {
  const [state, load] = useLoad(() => finApi.get("/landlord/pay"), []);
  const [n, setN] = useState(0);
  const d = state.data;
  const changed = () => { setN((x) => x + 1); load(); onChanged?.(); };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0B1220] text-nex-silver max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden grid-cols-1 [&>*]:min-w-0" data-testid="pay-ll-dialog">
        <DialogHeader><DialogTitle className="text-nex-silver">Pay landlord</DialogTitle></DialogHeader>
        {state.loading && !d && <Loading testid="pay-ll-loading" />}
        {state.error && <ErrorBox testid="pay-ll-error" error={state.error} onRetry={load} />}
        {d && <div className="space-y-4 min-w-0">
          <Owed o={d.owed} />
          <PayForm key={n} d={d} onSaved={changed} prefill={n ? null : prefill} />
          <div className="pt-2 border-t border-white/10 space-y-2"><div className="text-xs text-nex-silver">Payments recorded here</div><Recent rows={d.recent} onChanged={changed} /></div>
        </div>}
      </DialogContent>
    </Dialog>
  );
}

/** "Pay landlord" button + dialog. `as="span"` lets it sit inside a clickable card. */
export function PayLandlordButton({ as = "button", onChanged, testid = "pay-landlord-btn", label = "Pay landlord", prefill }) {
  const [open, setOpen] = useState(false);
  const Tag = as;
  const click = (e) => { e.stopPropagation(); e.preventDefault(); setOpen(true); };
  return (
    <>
      <Tag {...(as === "button" ? { type: "button" } : { role: "button", tabIndex: 0 })} onClick={click} onKeyDown={(e) => e.key === "Enter" && click(e)} data-testid={testid}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-nex-gold/50 text-nex-gold font-mono text-[10px] uppercase tracking-[0.14em] hover:bg-nex-gold/10 transition-colors cursor-pointer">
        <Landmark className="h-3 w-3" />{label}
      </Tag>
      {open && <span onClick={(e) => e.stopPropagation()}><PayLandlordDialog onClose={() => setOpen(false)} onChanged={onChanged} prefill={prefill} /></span>}
    </>
  );
}
